import React, { useEffect, useState, FC } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { PusherProvider } from './contexts/PusherContext'; // Import the PusherProvider
import { Providers as DependencyProviders } from './providers'; // Import our new providers
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ConfirmUserCompanyResponse } from '@dreamai/types';
import './utils/testDependencyUpdate'; // Load test utilities in development

import AppLoading from './components/misc/AppLoading';
import Navigation from './components/Navigation';
import FunnelDashboard from './components/FunnelDashboard';
import AdvancedMode from './components/funnel/AdvancedMode';
import Funnel from './components/Funnel';
import Dashboard from './components/Dashboard';
import NewDashboard from './components/NewDashboard';
import GoLogin from './components/GoLogin';
import CreateCompany from './components/CreateCompany';
import Settings from './components/Settings';
import Profiles from './components/Profiles';
import Profile from './components/profiles/Profile';
import Emails from './components/Emails';
import EditSequence from './components/emails/EditSequence';
import IntegrationsComplete from './components/settings/IntegrationsComplete';
import Product from './components/products/Product';
import Products from './components/Products';
import Debug from './components/Debug';
import KnownOrg from './components/analyst/KnownOrg';
import api from './api';

import Analyst from './components/Analyst';
import Prospect from './components/Prospect';
import UIUpdatePage from './components/ui-updates/UIUpdatePage';

import Campaigns from './components/Campaigns';
import Campaign from './components/campaigns/Campaign';
import CampaignWizard from './components/campaigns/CampaignWizard';
import CampaignEditMessaging from './components/campaigns/CampaignEditMessaging';
import CampaignEditProspects from './components/campaigns/CampaignEditProspects';
import CampaignEditCompanies from './components/campaigns/CampaignEditCompanies';
import CampaignEditSettings from './components/campaigns/CampaignEditSettings';
import Snapshots from './components/snapshots/Snapshots';

import AdminDashboard from './components/AdminDashboard';
import TaskListPage from './components/tasks/TaskListPage';
import ManageCampaign from './components/campaigns/ManageCampaign';

import { useSidebar } from './contexts/SidebarContext';
import { ThreadLoadingProvider } from './contexts/ThreadLoadingContext';
import { MessagingProvider } from './contexts/MessagingContext';
import { NewThreadProvider } from './contexts/NewThreadContext';
import { FormCaptureProvider } from './contexts/FormCaptureContext';
import { CommentsProvider } from './contexts/CommentsContext';
import { LinkedThreadsProvider } from './contexts/LinkedThreadsContext';

import StatusCheckWrapper from './components/misc/StatusCheckWrapper';
import './index.scss';
import ConversationalUI from './components/ConversationalUI';
import BookingPage from './components/BookingPage';
import WebRTCVoiceChat from './components/WebRTCVoiceChat';
import { CompanyProvider, useCompany } from './contexts/CompanyContext';

const AppContent: FC = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [isConversationalUI, setIsConversationalUI] = useState<boolean>(false);
  const { setMemoryEnabled } = useCompany();

  const { isSidebarVisible, imposedWarnings } = useSidebar();
  const navigate = useNavigate();

  useEffect(() => {
    const checkCompanyStatus = async (): Promise<void> => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          const response = await api.get<ConfirmUserCompanyResponse>(
            '/confirm-user-company',
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          setHasCompany(response.data.has_company);
          setIsConversationalUI(response.data.conversational_ui || false);
          setMemoryEnabled(response.data.memory_enabled || false);

          if (!response.data.has_company) {
            navigate('/create-company');
          }
        } catch (error) {
          console.error('Error checking company status:', error);
        }
      }
    };

    checkCompanyStatus();
  }, [isAuthenticated, navigate, getAccessTokenSilently]);

  if (!isLoading && !isAuthenticated) {
    // Check if this is a booking page route - allow access without authentication
    if (window.location.pathname.startsWith('/booking/')) {
      return (
        <Routes>
          <Route path='/booking/:key' element={<BookingPage />} />
          <Route path='*' element={<GoLogin />} />
          <Route path='/voice-chat' element={<WebRTCVoiceChat />} />
        </Routes>
      );
    }
    return <GoLogin />;
  }

  if (isAuthenticated && hasCompany !== null) {
    // Render conversational UI if the company has that feature enabled
    if (isConversationalUI) {
      return (
        <PusherProvider>
          <DependencyProviders>
            <MessagingProvider>
              <NewThreadProvider>
                <ThreadLoadingProvider>
                  <FormCaptureProvider>
                    <CommentsProvider>
                      <LinkedThreadsProvider>
                        <ConversationalUI />
                      </LinkedThreadsProvider>
                    </CommentsProvider>
                  </FormCaptureProvider>
                </ThreadLoadingProvider>
              </NewThreadProvider>
            </MessagingProvider>
          </DependencyProviders>
        </PusherProvider>
      );
    }

    // Standard UI for companies without conversational_ui enabled
    return (
      <PusherProvider>
        <div className='global-container'>
          <div
            id={`sidebar-wrapper`}
            className={`${!isSidebarVisible ? 'hidden' : 'visible'}`}
          >
            <Navigation imposedWarnings={imposedWarnings} />
          </div>
          <div id='page-content-wrapper'>
            <Routes>
              <Route path='/' element={<NewDashboard />} />
              <Route path='/admin-dashboard' element={<AdminDashboard />} />
              <Route path='/dashboard' element={<NewDashboard />} />
              <Route path='/old-dashboard' element={<Dashboard />} />
              <Route path='/task-list' element={<TaskListPage />} />
              <Route path='/task-list/:campaignId' element={<TaskListPage />} />
              <Route path='/funnel' element={<FunnelDashboard />} />
              <Route path='/advanced-mode' element={<AdvancedMode />} />
              <Route path='/campaigns' element={<Campaigns />} />
              <Route path='/campaign-edit/:id' element={<ManageCampaign />} />
              <Route
                path='/campaign-edit/:id/:step'
                element={<ManageCampaign />}
              />
              <Route path='/campaign/:id' element={<Campaign />} />
              <Route
                path='/campaign/:id/messaging'
                element={<CampaignEditMessaging />}
              />
              <Route
                path='/campaign-prospects/:id'
                element={<CampaignEditProspects />}
              />
              <Route
                path='/campaign-companies/:id'
                element={<CampaignEditCompanies />}
              />
              <Route
                path='/campaign-settings/:id'
                element={<CampaignEditSettings />}
              />
              <Route path='/campaign-wizard/:id' element={<CampaignWizard />} />
              <Route path='/prospect/:id' element={<Prospect />} />
              <Route path='/profiles' element={<Profiles />} />
              <Route path='/profiles/:id' element={<Profile />} />
              <Route path='/funnel/:id' element={<Funnel />} />
              <Route path='/settings' element={<Settings />} />
              <Route path='/settings/:setting' element={<Settings />} />
              <Route path='/create-company' element={<CreateCompany />} />
              <Route path='/emails' element={<Emails />} />
              <Route
                path='/emails/:sequenceId/edit'
                element={<EditSequence />}
              />
              <Route
                path='/integrations-complete'
                element={<IntegrationsComplete />}
              />
              <Route path='/products' element={<Products />} />
              <Route path='/products/:id' element={<Product />} />
              <Route path='/marauders' element={<Debug />} />
              <Route path='/analyst' element={<Analyst />} />
              <Route path='/analyst/:id' element={<KnownOrg />} />
              <Route path='/snapshots' element={<Snapshots />} />
              <Route path='/ui-updates' element={<UIUpdatePage />} />
            </Routes>
          </div>
          <ToastContainer className='toast-container-wrapper' />
          <StatusCheckWrapper />
        </div>
      </PusherProvider>
    );
  }

  return <AppLoading />;
};

const App: FC = () => {
  return (
    <CompanyProvider>
      <AppContent />
    </CompanyProvider>
  );
};

export default App;
